import * as React from 'react'
import { MONEY_AMOUNT_PATTERN, formatMoney } from '@lib/helpers/money'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

export interface PriceOverrideProps {
  /** Unique on the sheet, for the field's id. */
  id: string
  /** What is priced: "Pruning price", "Removal price". */
  label: string
  /** What the formula works out to, in cents; null when it prices nothing. */
  formulaCents: number | null
  /** How the formula got there, finishing "$350.00 …": "at the crew rate". */
  formulaBasis: string
  /** What to say in place of a figure when the formula prices nothing. */
  unpricedNote: string
  /** The price set by hand, in dollars, or null for the formula. */
  value: string | null
  controlClassName?: string | undefined
  onChange: (value: string | null) => void
}

/** Whether typed text is a price, the same test the API applies. */
function isPrice(text: string): boolean {
  return MONEY_AMOUNT_PATTERN.test(text.trim())
}

/**
 * A price the formula works out, with a way to set it by hand.
 *
 * At rest it is one line: the figure the formula gives and a "Set price"
 * link. Most trees are priced right by the formula, and a dollar field on
 * every one would invite somebody to type over a price that was fine. Once a
 * price is set it shows as a field, with the formula's figure underneath, so
 * the person standing under the tree can see how far from the formula they
 * have gone.
 *
 * Only the money changes. The minutes stay what they are, because the crew is
 * scheduled by them (docs/proposals.md).
 */
export function PriceOverride({
  id,
  label,
  formulaCents,
  formulaBasis,
  unpricedNote,
  value,
  controlClassName,
  onChange,
}: PriceOverrideProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  /** Local text, so a half-typed "85." can exist without being committed. */
  const [text, setText] = React.useState(value ?? '')
  /** Opened by "Set price" before anything has been typed. */
  const [editing, setEditing] = React.useState(false)

  React.useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const formula =
    formulaCents === null ? null : `${formatMoney(formulaCents)} ${formulaBasis}`
  const unreadable = text.trim() !== '' && !isPrice(text)

  /** Only a well-formed price is committed; empty goes back to the formula. */
  function commit(raw: string): void {
    setText(raw)
    const trimmed = raw.trim()
    if (trimmed === '') {
      onChange(null)
      return
    }
    if (isPrice(trimmed)) onChange(trimmed)
  }

  /** The line under the field: what was mistyped, or what this replaces. */
  function note(): string {
    if (unreadable) return 'Enter a price in dollars, e.g. 850 or 850.50.'
    if (formula === null) return 'Set by hand. The formula prices nothing here.'
    return `Set by hand, in place of ${formula}.`
  }

  /** What the link beside the label does: back out, or back to the formula. */
  function resetLabel(): string {
    if (value === null) return 'Cancel'
    return formulaCents === null ? 'Clear price' : 'Use the formula'
  }

  function reset(): void {
    setText('')
    setEditing(false)
    onChange(null)
  }

  if (value === null && !editing) {
    return (
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="min-w-0 text-secondary-text">
          {label}:{' '}
          {formula === null ? (
            <span className="text-muted-text">{unpricedNote}</span>
          ) : (
            <span className="font-medium tabular-nums text-primary-text">
              {formula}
            </span>
          )}
        </span>
        <button
          type="button"
          // Padded past its text, then pulled back, so a gloved thumb has
          // more to hit than a line of 12px type without moving anything.
          className="-my-2 shrink-0 py-2 pl-2 font-medium text-brand hover:underline"
          onClick={() => {
            setEditing(true)
          }}
        >
          Set price
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <button
          type="button"
          className="-my-2 py-2 pl-2 text-xs font-medium text-brand hover:underline"
          onClick={reset}
        >
          {resetLabel()}
        </button>
      </div>
      <div className="relative">
        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-muted-text">
          $
        </span>
        <Input
          ref={inputRef}
          id={id}
          inputMode="decimal"
          placeholder={
            formulaCents === null ? '0.00' : (formulaCents / 100).toFixed(2)
          }
          aria-invalid={unreadable}
          aria-describedby={`${id}-note`}
          className={cn(controlClassName, 'pl-6 tabular-nums')}
          value={text}
          onChange={(event) => {
            commit(event.target.value)
          }}
          onBlur={() => {
            if (text.trim() === '') setEditing(false)
          }}
        />
      </div>
      <p
        id={`${id}-note`}
        className={cn('text-xs', unreadable ? 'text-danger' : 'text-muted-text')}
      >
        {note()}
      </p>
    </div>
  )
}
